/**
 * Web Client Internationalization (i18n) Configuration
 *
 * Initializes react-i18next with browser language detection and HTTP backend
 * for loading translation files from the public/locales directory.
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

/**
 * Supported languages with their display labels and optional flag codes.
 */
export const SUPPORTED_LANGUAGES = {
  en: { label: 'English', flag: 'US' },
  es: { label: 'Espa\u00f1ol', flag: 'ES' },
  de: { label: 'Deutsch', flag: 'DE' },
  fr: { label: 'Fran\u00e7ais', flag: 'FR' },
  zh: { label: '\u4e2d\u6587', flag: 'CN' },
  ja: { label: '\u65e5\u672c\u8a9e', flag: 'JP' },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export const NAMESPACES = [
  'common',
  'dashboard',
  'inventory',
  'production',
  'recipes',
  'settings',
  'reports',
  'supplychain',
  'wizard',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export const DEFAULT_NAMESPACE: Namespace = 'common';

/**
 * localStorage key used to persist the user's language preference.
 */
export const LANGUAGE_STORAGE_KEY = 'stockforge-language';

i18n
  // Load translations via HTTP from public/locales/
  .use(HttpBackend)
  // Detect browser language
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  .init({
    // Language settings
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: Object.keys(SUPPORTED_LANGUAGES),

    // Namespace settings
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,

    // HTTP backend settings - load from public/locales/
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    // Language detection settings
    detection: {
      // Detection order: localStorage > navigator > htmlTag
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },

    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes by default
    },

    // React-specific settings
    react: {
      useSuspense: true,
    },

    // Debug mode in development
    debug: import.meta.env.DEV && import.meta.env.VITE_I18N_DEBUG === 'true',
  });

export default i18n;
